// service/Hooks/useExecutionNotices.js
import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import ExecutionNoticeService from '../api/ExecutionNoticeService';
import { useLoader } from './useLoader';

const useExecutionNotices = (domainId, accountId = null) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [executionNotices, setExecutionNotices] = useState({});
    const { showLoader, hideLoader, setProgress } = useLoader();


  // Récupérer automatiquement les avis existants
  useEffect(() => {
    if (domainId && accountId) {
      loadClientNotices(domainId, accountId);
    }
  }, [domainId, accountId]);

  const loadClientNotices = useCallback(async (domainId, accountId) => {
    try {
      const result = await ExecutionNoticeService.getClientExecutionNotices(domainId, accountId);
      if (result.success && result.data) {
        // Transformer la liste en objet pour un accès facile par transactionId
        const noticesMap = {};
        result.data.forEach(notice => {
          if (notice.transactionId) {
            noticesMap[notice.transactionId] = notice;
          }
        });
        setExecutionNotices(noticesMap);
      }
    } catch (err) {
      // console.error('Error loading execution notices:', err);
    }
  }, []);

  // Générer un avis d'exécution
  const generateNotice = useCallback(async (domainId, transactionId) => {
    setLoading(true);
    setError(null);
    showLoader({ message: "Génération de l'avis d'execution en cours...", indeterminate: true });
    
    try {
      const result = await ExecutionNoticeService.generateExecutionNotice(domainId, transactionId);
      
      if (result.success) {
        // Mettre à jour l'état local avec le nouvel avis
        setExecutionNotices(prev => ({
          ...prev,
          [transactionId]: {
            id: result.noticeId,
            noticeNumber: result.noticeNumber,
            status: 'Generated',
            transactionId: transactionId
          }
        }));
        
        toast.success(result.message || 'Avis d\'exécution généré avec succès');
        return result;
      } else {
        // Si l'avis existe déjà, le récupérer automatiquement
        if (result.message?.includes('existe déjà')) {
          await loadExistingNotice(domainId, transactionId);
          return { success: true, message: 'Avis déjà disponible' };
        }
        
        toast.error(result.message || 'Erreur lors de la génération');
        setError(result.message);
        return result;
      }
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors de la génération de l\'avis';
      
      // Si l'erreur indique que l'avis existe déjà
      if (errorMessage.includes('existe déjà')) {
        await loadExistingNotice(domainId, transactionId);
        return { success: true, message: 'Avis déjà disponible' };
      }
      
      toast.error(errorMessage);
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
      hideLoader();
    }
  }, []);

  // Charger un avis existant
  const loadExistingNotice = useCallback(async (domainId, transactionId) => {
    
    try {
      const result = await ExecutionNoticeService.getClientExecutionNotices(domainId, null);
      if (result.success && result.data) {
        const existingNotice = result.data.find(notice => notice.transactionId === transactionId);
        if (existingNotice) {
          setExecutionNotices(prev => ({
            ...prev,
            [transactionId]: existingNotice
          }));
        }
      }
    } catch (err) {
      // console.error('Error loading existing notice:', err);
    }
  }, []);

  // Télécharger un avis
  const downloadNotice = useCallback(async (domainId, noticeId, fileName = null) => {
    setLoading(true);
    showLoader({ message: "Telecharger de l'avis d'execution en cours...", indeterminate: true });

    try {
      const blob = await ExecutionNoticeService.downloadExecutionNotice(domainId, noticeId);
      
      // Créer un URL pour le blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `avis-execution-${noticeId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Avis téléchargé avec succès');
      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors du téléchargement';
      toast.error(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
      hideLoader();
    }
  }, []);

  // Visualiser un avis
  const viewNotice = useCallback(async (domainId, noticeId) => {
    setLoading(true);
    showLoader({ message: "Voir de l'avis d'execution en cours...", indeterminate: true });
    try {
      const result = await ExecutionNoticeService.getExecutionNoticeViewUrl(domainId, noticeId);
      
      if (result.success) {
        // Ouvrir dans un nouvel onglet
        window.open(result.viewUrl, '_blank');
        return result;
      } else {
        toast.error(result.message || 'Erreur lors de l\'ouverture');
        return result;
      }
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors de la visualisation';
      toast.error(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
      hideLoader();
    }
  }, []);

  // Envoyer par email
  const sendNoticeByEmail = useCallback(async (domainId, noticeId) => {
    setLoading(true);
    showLoader({ message: "Envoi d'email de l'avis d'execution en cours...", indeterminate: true });
    
    try {
      const result = await ExecutionNoticeService.sendExecutionNoticeByEmail(domainId, noticeId);
      
      if (result.success) {
        toast.success(result.message || 'Avis envoyé par email avec succès');
        return result;
      } else {
        toast.error(result.message || 'Erreur lors de l\'envoi');
        return result;
      }
    } catch (err) {
      const errorMessage = err.message || 'Erreur lors de l\'envoi de l\'email';
      toast.error(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
      hideLoader();
    }
  }, []);

  // Vérifier si un avis existe pour une transaction
  const hasExecutionNotice = useCallback((transactionId) => {
    return !!executionNotices[transactionId];
  }, [executionNotices]);

  // Obtenir un avis spécifique
  const getExecutionNotice = useCallback((transactionId) => {
    return executionNotices[transactionId];
  }, [executionNotices]);

  return {
    loading,
    error,
    executionNotices,
    generateNotice,
    downloadNotice,
    viewNotice,
    sendNoticeByEmail,
    hasExecutionNotice,
    getExecutionNotice,
    clearError: () => setError(null),
    refreshNotices: (domainId, accountId) => loadClientNotices(domainId, accountId)
  };
};

export default useExecutionNotices;